import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { baseAPI } from '../../config';
import userStore from '../../store/userStore';
import { canConnectWhatsapp } from '@claimup/shared/helpers/permissions';
import { ChatBubbleLeftRightIcon, PhoneIcon } from '@heroicons/react/24/outline';
import CallPhonesSection from './CallPhonesSection';
import CompanyEmailBlock from '../company-email/CompanyEmailBlock';

// Bloque "Comunicación" del perfil de una empresa. Lo ve tanto un admin de
// ClaimUp como la propia empresa, así que todo lo que se muestre aquí tiene que
// tener sentido para las dos.
//
// Dos canales conviven con reglas distintas:
//   - WhatsApp oficial (Kapso): lo conecta el cliente. Es lo que se usa en AR.
//   - Maytapi / Aircall: números de ClaimUp, solo lectura desde aquí.
//
// Y el correo (`showEmail`): el dominio de envío de AR. Solo lo pasa
// `AccountPanel` para admin o el dueño de la company (`canManageCompanyEmail`).

/**
 * A partir de cuántos días un alta a medias deja de ser «paciencia».
 *
 * El proceso de Meta se hace de una sentada: quien lo empieza y no lo termina en
 * el día, casi siempre es porque algo lo cortó. Tres días da margen de sobra al
 * cliente que lo dejó para el lunes, sin tener a nadie un mes mirando
 * «Conectando» sin saber que la pelota es suya.
 */
const DIAS_PARA_SOSPECHAR = 3;

/** Días que lleva esperando este alta, o null si no sabemos cuándo empezó. */
const diasEsperando = n => {
  if (!n.setup_link_created_at) return null;
  const ms = Date.now() - new Date(n.setup_link_created_at).getTime();
  return Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
};

const ESTADOS_KAPSO = {
  pending: {
    etiqueta: 'Conectando',
    clase: 'bg-amber-50 text-amber-700 border-amber-200',
    punto: 'bg-amber-400',
  },
  connected: {
    etiqueta: 'Conectado',
    clase: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    punto: 'bg-emerald-500',
  },
  failed: {
    etiqueta: 'No se pudo conectar',
    clase: 'bg-red-50 text-red-700 border-red-200',
    punto: 'bg-red-500',
  },
  disconnected: {
    etiqueta: 'Desconectado',
    clase: 'bg-slate-100 text-slate-600 border-slate-200',
    punto: 'bg-slate-400',
  },
};

// El estado de un número de Maytapi. Hasta ahora no se mostraba, así que una
// empresa podía ver un número bloqueado como si funcionase.
const ESTADOS_MAYTAPI = {
  active: { etiqueta: 'Activo', clase: 'text-emerald-600' },
  blocked: { etiqueta: 'Bloqueado', clase: 'text-red-600' },
  deleted: { etiqueta: 'Eliminado', clase: 'text-slate-400' },
};

const Etiqueta = ({ estado }) => {
  const e = ESTADOS_KAPSO[estado] || ESTADOS_KAPSO.disconnected;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium ${e.clase}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${e.punto}`} />
      {e.etiqueta}
    </span>
  );
};

const CompanyPhonesBlock = ({ userData, showEmail = false, isAdmin = false }) => {
  const companyId = userData?._id;
  const { user: currentUser } = userStore();

  const [phones, setPhones] = useState({ maytapi: [], aircall: [], kapso: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [errorCarga, setErrorCarga] = useState(null);
  const [conectando, setConectando] = useState(false);
  const [dandoDeBaja, setDandoDeBaja] = useState(null);

  /**
   * Da de baja un número: el cliente se va, o conectó el que no era.
   *
   * Se confirma antes porque no se deshace desde aquí —habría que volver a
   * pasar por Meta— y porque a partir de ese momento esa empresa deja de poder
   * escribir a sus deudores por ese número.
   */
  const darDeBaja = async n => {
    const cual = n.phone_number || n.name || 'este número';
    if (
      !window.confirm(
        `¿Dar de baja ${cual}?\n\nDejará de usarse para enviar mensajes. El historial se conserva, ` +
          `y volver a conectarlo exige repetir el proceso en Meta.`
      )
    ) {
      return;
    }

    setDandoDeBaja(n.phone_number_id);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${baseAPI}/kapso/companies/${companyId}/phones/${n.phone_number_id}/disconnect`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success('Número dado de baja');
      await cargar();
    } catch (err) {
      toast.error(err?.response?.data?.error?.message || 'No se ha podido dar de baja');
    } finally {
      setDandoDeBaja(null);
    }
  };

  const puedeConectar = canConnectWhatsapp(currentUser?.role, currentUser?.id, companyId);

  /**
   * Si quien mira es la propia empresa o un admin mirando la ficha de un
   * cliente. Cambia a quién se le habla: desde que el auto-servicio está
   * abierto, este bloque lo leen los dos, y decirle «la empresa» y «su móvil» a
   * alguien que está mirando SU propio número suena a que hablamos de otro.
   */
  const esPropia = String(currentUser?.id) === String(companyId);

  const cargar = useCallback(() => {
    if (!companyId) return;
    const token = localStorage.getItem('token');
    return axios
      .get(`${baseAPI}/settings/company-phones/${companyId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then(res => {
        setPhones({ maytapi: [], aircall: [], kapso: [], ...(res.data?.data || {}) });
        setErrorCarga(null);
      })
      // Antes se enseñaba «esta empresa no tiene WhatsApp conectado», que es
      // otra cosa. Para un usuario que solo puede mirar, esta pantalla es su
      // única fuente: decirle que no hay números cuando lo que pasa es que no
      // los hemos podido leer es mentirle.
      .catch(err => {
        setErrorCarga(
          err?.response?.status === 403
            ? 'No tienes permiso para ver los números de esta empresa.'
            : 'No se han podido cargar los números.'
        );
      })
      .finally(() => setIsLoading(false));
  }, [companyId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // `forzarNueva` distingue "quiero OTRO número" de "he pulsado dos veces". Sin
  // esa señal el backend devuelve el enlace que ya había, que es lo correcto
  // ante un doble clic pero no cuando la empresa quiere una segunda línea.
  const conectarWhatsapp = async ({ forzarNueva = false } = {}) => {
    setConectando(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${baseAPI}/kapso/companies/${companyId}/setup-link`,
        forzarNueva ? { force_new: true } : {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const url = res.data?.data?.url;
      if (!url) throw new Error('Kapso no devolvió ningún enlace');
      await cargar();
      // Se abre en otra pestaña: el proceso ocurre en la web de Meta y puede
      // durar varios minutos. Si se navegara fuera, el usuario perdería esta
      // pantalla, que es donde va a ver en qué punto está.
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'No se pudo iniciar la conexión';
      toast.error(msg);
    } finally {
      setConectando(false);
    }
  };

  const { maytapi = [], aircall = [], kapso = [] } = phones;

  // El botón nunca desaparece, pero significa tres cosas distintas según lo que
  // haya. Kapso solo admite UN enlace activo por empresa —al emitir uno nuevo
  // revoca el anterior—, así que mientras hay un alta pendiente la única acción
  // posible es reemplazar su enlace, no abrir otra en paralelo. Un segundo
  // número se pide cuando el primero ya está conectado.
  const hayAlgunaFicha = kapso.length > 0;
  const hayPendiente = kapso.some(n => n.connection_status === 'pending');

  const textoBoton = hayPendiente
    ? 'Generar enlace nuevo'
    : hayAlgunaFicha
      ? 'Añadir otro número'
      : 'Conectar WhatsApp';

  const MaytapiRow = ({ name, number, status }) => {
    const e = ESTADOS_MAYTAPI[status];
    return (
      <div className='flex items-center justify-between px-4 py-2 border border-slate-200 rounded-lg bg-white'>
        <div className='flex items-center gap-2'>
          <span className='text-sm font-medium text-slate-800'>{name || 'Sin nombre'}</span>
          {e && status !== 'active' && (
            <span className={`text-xs font-medium ${e.clase}`}>· {e.etiqueta}</span>
          )}
        </div>
        <span className='text-sm text-slate-500 font-mono'>{number}</span>
      </div>
    );
  };

  const KapsoRow = ({ n }) => {
    const caducado =
      n.setup_link_expires_at && new Date(n.setup_link_expires_at) < new Date();

    return (
      <div className='px-4 py-3 border border-slate-200 rounded-lg bg-white space-y-2'>
        <div className='flex items-center justify-between gap-3'>
          <span className='text-sm font-medium text-slate-800'>
            {n.phone_number || n.name || 'Número pendiente de confirmar'}
          </span>
          <Etiqueta estado={n.connection_status} />
        </div>

        {n.connection_status === 'pending' && (
          <div className='text-sm text-slate-500'>
            {!n.setup_link_url || caducado ? (
              <span className='text-amber-700'>
                El enlace de configuración ya no es válido. Genera uno nuevo para continuar.
              </span>
            ) : (
              <>
                Esperando a que se complete el proceso en Meta
                {diasEsperando(n) !== null && (
                  <> desde hace {diasEsperando(n) === 0 ? 'hoy' : `${diasEsperando(n)} día${diasEsperando(n) > 1 ? 's' : ''}`}</>
                )}
                .{' '}
                <a
                  href={n.setup_link_url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-blue-600 hover:underline font-medium'
                >
                  Continuar donde lo dejaste
                </a>
                {/* Pasados unos días, «esperando» deja de ser una explicación.
                    Lo normal es que el proceso de Meta se cortara: con una
                    cuenta que no puede usar WhatsApp, Meta corta justo al final
                    y aquí no llega ningún error — Kapso no se entera siquiera.
                    Sin esto, el cliente se queda mirando «Conectando» 30 días
                    sin saber que la pelota es suya. */}
                {diasEsperando(n) >= DIAS_PARA_SOSPECHAR && (
                  <p className='mt-2 text-amber-700'>
                    Lleva {diasEsperando(n)} días sin completarse. Normalmente significa que el
                    proceso en Meta se interrumpió: revisa que la cuenta de Meta pueda usar
                    WhatsApp Business y que elegiste la cuenta de negocio correcta. Si sigue sin
                    avanzar, escríbenos.
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {n.connection_status === 'failed' && (
          <p className='text-sm text-red-600'>
            {n.kapso_setup_error || 'Meta rechazó la conexión, sin motivo detallado.'}
          </p>
        )}

        {n.connection_status === 'connected' && n.is_coexistence === false && (
          <p className='text-xs text-slate-500'>
            Este número se migró por completo: ya no funciona en la app de WhatsApp del móvil.
          </p>
        )}

        {n.connection_status === 'disconnected' && (
          <p className='text-xs text-slate-500'>
            Dado de baja
            {n.disconnected_at &&
              ` el ${new Date(n.disconnected_at).toLocaleDateString('es-ES')}`}
            {n.disconnected_reason && `. ${n.disconnected_reason}`}
            {'. '}
            <span className='text-slate-400'>
              Se conserva para que el historial de mensajes siga teniendo emisor.
            </span>
          </p>
        )}

        {puedeConectar && n.connection_status === 'connected' && (
          <button
            type='button'
            onClick={() => darDeBaja(n)}
            disabled={dandoDeBaja === n.phone_number_id}
            className='text-xs font-medium text-red-700 hover:underline disabled:text-slate-400'
          >
            {dandoDeBaja === n.phone_number_id ? 'Dando de baja…' : 'Dar de baja este número'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className='bg-white rounded-lg border border-slate-200 overflow-hidden'>
      <div className='px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center'>
        <ChatBubbleLeftRightIcon className='h-5 w-5 text-blue-600 mr-2' />
        <h3 className='text-lg font-medium text-slate-900'>Comunicación</h3>
      </div>

      <div className='p-6 space-y-6'>
        {isLoading ? (
          <div className='flex justify-center py-4'>
            <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500'></div>
          </div>
        ) : errorCarga ? (
          <div className='rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700'>
            {errorCarga}{' '}
            <button type='button' onClick={cargar} className='underline font-medium'>
              Reintentar
            </button>
          </div>
        ) : (
          <>
            {/* WhatsApp oficial */}
            <div>
              <div className='flex items-center justify-between mb-2'>
                <div className='flex items-center'>
                  <ChatBubbleLeftRightIcon className='h-4 w-4 text-slate-400 mr-1.5' />
                  <span className='text-sm font-medium text-slate-600'>WhatsApp</span>
                </div>
                {puedeConectar && (
                  <button
                    type='button'
                    onClick={() => conectarWhatsapp({ forzarNueva: hayAlgunaFicha })}
                    disabled={conectando}
                    className='text-sm font-medium px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    {conectando ? 'Preparando…' : textoBoton}
                  </button>
                )}
              </div>

              {kapso.length === 0 ? (
                <p className='text-sm text-slate-500'>
                  Esta empresa todavía no tiene WhatsApp conectado.{' '}
                  <span className='text-slate-600'>
                    Sin un número propio no se pueden enviar mensajes en sus expedientes.
                  </span>
                </p>
              ) : (
                <div className='space-y-2'>
                  {kapso.map(n => (
                    <KapsoRow key={n._id} n={n} />
                  ))}
                </div>
              )}

              {/* Qué va a pasar al pulsar, ANTES de pulsar.
                  Comprobado el 04/09/2026 haciendo el alta de verdad: lo primero
                  que aparece es un login de Facebook, y descoloca — el cliente
                  cree que le pedimos su Facebook privado.

                  Y hay algo peor que la sorpresa: si el enlace lo abre alguien
                  del cliente que NO administra el negocio, el proceso muere ahí
                  y para nosotros la ficha se queda en «Conectando», igual que si
                  lo hubieran dejado a medias. Por eso se dice quién tiene que
                  hacerlo.

                  Se enseña siempre que se pueda conectar, no solo la primera
                  vez: al añadir un segundo número pasa exactamente lo mismo. */}
              {puedeConectar && (
                <div className='mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 space-y-1.5'>
                  <p className='font-medium text-slate-700'>Qué va a pasar al pulsar</p>
                  <p>
                    Se abre la web de Meta y <strong>lo primero que pide es iniciar sesión en
                    Facebook</strong>, con la cuenta personal que administra el negocio.
                  </p>
                  <p>
                    {esPropia ? (
                      <>
                        Tiene que hacerlo esa persona: si lo abre alguien sin acceso a esa
                        cuenta, el proceso se queda a medias. Si aún no tienes cuenta de negocio,
                        se crea en ese mismo paso.
                      </>
                    ) : (
                      <>
                        <strong>Manda el enlace a quien administre su Facebook de empresa</strong>,
                        no a cualquier contacto: si lo abre alguien sin acceso, el alta se queda
                        a medias y aquí seguirá saliendo «Conectando». Si no tienen cuenta de
                        negocio, la crean en ese mismo paso.
                      </>
                    )}
                  </p>
                  <p className='text-slate-500'>
                    El número seguirá funcionando en {esPropia ? 'tu' : 'su'} móvil con normalidad.
                  </p>
                </div>
              )}
            </div>

            {/* Números de ClaimUp (recobro) */}
            {(maytapi.length > 0 || aircall.length > 0) && (
              <div className='pt-4 border-t border-slate-200 space-y-4'>
                {maytapi.length > 0 && (
                  <div>
                    <span className='text-sm font-medium text-slate-600'>
                      WhatsApp de recobro (ClaimUp)
                    </span>
                    <div className='space-y-2 mt-2'>
                      {maytapi.map(p => (
                        <MaytapiRow key={p.phone_id} name={p.name} number={p.phone_number} status={p.status} />
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <div className='flex items-center mb-2'>
                    <PhoneIcon className='h-4 w-4 text-slate-400 mr-1.5' />
                    <span className='text-sm font-medium text-slate-600'>Llamadas (Aircall)</span>
                  </div>
                  {aircall.length === 0 ? (
                    <p className='text-sm text-slate-400'>Sin número de llamadas propio.</p>
                  ) : (
                    <div className='space-y-2'>
                      {aircall.map(p => (
                        <MaytapiRow key={p.phone_id} name={p.nombre} number={p.phone_number} status={p.status} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/*
          Fuera del bloque de arriba, y es deliberado por dos motivos. Uno: ese
          bloque solo se pinta si la empresa YA tiene algún número, y quien no
          tiene ninguno es justo quien necesita poder añadir el suyo. Y dos:
          queda también fuera del estado de carga y del de error, así que si la
          consulta de teléfonos falla, el cliente sigue pudiendo dar de alta su
          número — esta sección trae sus propios datos y no depende de aquélla.
        */}
        <CallPhonesSection companyId={companyId} />

        {showEmail && (
          <div className='pt-4 border-t border-slate-200'>
            <CompanyEmailBlock userData={userData} isAdmin={isAdmin} embedded />
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyPhonesBlock;
